import { Request } from './request';
import { Response } from './response';
import { HttpClientInterface, RequestOptions } from '../../interfaces/components/http-client/http-client.interface';
import { HttpMethods, RequestInterface } from '../../interfaces/components/http-client/request.interface';
import { ResponseInterface } from '../../interfaces/components/http-client/response.interface';
import { mergeHeaders } from '../../utils/headers';
import { Injectable } from '../dependency-injection/decorators';

export type HttpClientOptions = {
  baseUri?: URL | string,
  headers?: Headers,
  credentials?: RequestCredentials
}

@Injectable()
export class HttpClient implements HttpClientInterface {
  private options: HttpClientOptions = {};
  private static defaultOptions: HttpClientOptions = {
    // credentials: 'include',
  };

  public constructor (options: HttpClientOptions = {}) {
    this.options = {
      ...HttpClient.defaultOptions,
      ...options,
      headers: mergeHeaders(HttpClient.defaultOptions.headers, options.headers),
    };
  }

  public async sendRequest (request: RequestInterface, credentials?: RequestCredentials): Promise<ResponseInterface> {
    return fetch(request.getUri().toString(), {
      ...this.options,
      headers: mergeHeaders(this.options.headers, request.getHeaders()),
      body: request.getBody(),
      method: request.getMethod(),
      credentials: credentials || this.options.credentials,
    }).then<ResponseInterface>(async (_fetchResponse) => new Response(
      _fetchResponse.status,
      _fetchResponse.url,
      _fetchResponse.headers,
      _fetchResponse.body,
      _fetchResponse.clone().json().catch(() => null), // for 204 requests
      _fetchResponse.statusText,
    ));
  }

  public createRequest (method: HttpMethods, uri: URL | string, options?: RequestOptions): RequestInterface {
    const request = new Request(method, this.createUri(uri));
    const innerOptions = { ...options };

    let body = options?.body;

    if (typeof innerOptions?.json !== 'undefined') {
      if (typeof body !== 'undefined' && body !== '') {
        throw Error('Define either the "json" or the "body" option, setting both is not supported.');
      }

      body = JSON.stringify(innerOptions.json);
      delete innerOptions.json;

      if (!request.hasHeader('content-type')) {
        request.withHeader('content-type', 'application/json');
      }
    }

    if (Object.prototype.toString.call(body) === '[object Object]') {
      body = new URLSearchParams(body as Record<string, string>).toString();

      if (!request.hasHeader('content-type')) {
        request.withHeader('content-type', 'application/x-www-form-urlencoded');
      }
    }

    if (!request.hasHeader('accept')) {
      request.withHeader('accept', '*/*');
    }

    if ([ 'GET', 'HEAD' ].includes(method) && body) {
      throw new Error('Request with GET/HEAD method cannot have body');
    }

    request.withBody(body as BodyInit ?? null);

    return request;
  }

  public createUri (uri: string | URL): URL {
    return new URL(uri, this.options.baseUri);
  }

  public withOptions (options: HttpClientOptions): HttpClientInterface {
    if (typeof options.headers !== 'undefined') {
      options.headers = new Headers(options.headers);
    }

    this.options = { ...HttpClient.defaultOptions, ...options };

    return this;
  }
}
